import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Maximize2, Ruler, SwatchBook } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { buildMuralQuoteUrl } from "@/lib/quotePrefill";
import { getRouteState } from "@/lib/navigation";

const UNIT_OPTIONS = [
  { id: "cm", label: "cm", factor: 0.01 },
  { id: "m", label: "m", factor: 1 },
  { id: "mm", label: "mm", factor: 0.001 },
];

function clampRate(rate) {
  return Math.min(35, Math.max(18, Math.round(rate)));
}

function buildMaterials(baseRate) {
  const standardRate = clampRate(baseRate || 18);
  return [
    {
      id: "standard",
      label: "Standard Matte",
      rate: standardRate,
      desc: "Classic non-woven wallpaper with a refined, low-sheen finish.",
    },
    {
      id: "peel",
      label: "Premium Peel & Stick",
      rate: 28,
      desc: "220gsm removable wallpaper with repositionable adhesive for cleaner installs and residue-free removal.",
    },
    {
      id: "vinyl",
      label: "Heavy Duty Vinyl",
      rate: clampRate(standardRate + 7),
      desc: "Durable surface for commercial spaces and high-traffic interiors.",
    },
  ];
}

export function MuralConfigurator({
  productTitle,
  baseRate = 18,
  ctaLabel = "Request Quote With These Measurements",
  testIdPrefix = "mural-config",
  onConfigChange,
}) {
  const location = useLocation();
  const materials = useMemo(() => buildMaterials(baseRate), [baseRate]);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [unit, setUnit] = useState("cm");
  const [material, setMaterial] = useState(materials[0].id);

  const unitFactor = UNIT_OPTIONS.find((option) => option.id === unit)?.factor || 0.01;
  const widthValue = parseFloat(width) || 0;
  const heightValue = parseFloat(height) || 0;
  const areaSqm = widthValue * unitFactor * (heightValue * unitFactor);
  const selectedMaterial = materials.find((item) => item.id === material) || materials[0];
  const estimatedPrice = areaSqm > 0 ? areaSqm * selectedMaterial.rate : 0;
  const dimensionsText =
    areaSqm > 0
      ? `${(widthValue * unitFactor).toFixed(1)}m × ${(heightValue * unitFactor).toFixed(1)}m`
      : "";
  const quoteHref = buildMuralQuoteUrl({
    productTitle,
    dimensionsText,
    materialLabel: selectedMaterial.label,
    estimatedPrice,
  });

  // Report config changes to parent (for variant wiring)
  useEffect(() => {
    if (onConfigChange) {
      onConfigChange({
        widthM: widthValue * unitFactor,
        heightM: heightValue * unitFactor,
        areaSqm,
        materialId: material,
        materialLabel: selectedMaterial.label,
        estimatedPrice,
        dimensionsText,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthValue, heightValue, unit, material]);

  return (
    <div
      className="rounded-sm border border-brand-border bg-brand-surface/40 p-6 md:p-7"
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-brand-gold">
            Made-to-Measure Estimator
          </p>
          <h2 className="mt-2 font-heading text-2xl text-brand-text tracking-tight">
            Price Your Wall
          </h2>
        </div>
        <div
          className="rounded-sm border border-brand-border bg-brand-bg/70 px-4 py-3 text-right"
          data-testid={`${testIdPrefix}-from-rate`}
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-brand-text-secondary/50">
            Starting from
          </p>
          <p className="mt-1 text-lg font-medium text-brand-text">£{materials[0].rate}/m²</p>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-[1fr_auto] md:items-end">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label data-testid={`${testIdPrefix}-width-group`}>
            <span className="block text-[11px] tracking-wide uppercase text-brand-text-secondary/70 mb-2">
              Wall Width
            </span>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              value={width}
              onChange={(event) => setWidth(event.target.value)}
              placeholder={unit === "cm" ? "e.g. 320" : unit === "m" ? "e.g. 3.2" : "e.g. 3200"}
              className="w-full bg-transparent border-b border-brand-border focus:border-brand-gold text-brand-text py-3 outline-none transition-colors duration-300 text-[15px] placeholder:text-brand-text-secondary/25"
              data-testid={`${testIdPrefix}-width-input`}
            />
          </label>
          <label data-testid={`${testIdPrefix}-height-group`}>
            <span className="block text-[11px] tracking-wide uppercase text-brand-text-secondary/70 mb-2">
              Wall Height
            </span>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              value={height}
              onChange={(event) => setHeight(event.target.value)}
              placeholder={unit === "cm" ? "e.g. 240" : unit === "m" ? "e.g. 2.4" : "e.g. 2400"}
              className="w-full bg-transparent border-b border-brand-border focus:border-brand-gold text-brand-text py-3 outline-none transition-colors duration-300 text-[15px] placeholder:text-brand-text-secondary/25"
              data-testid={`${testIdPrefix}-height-input`}
            />
          </label>
        </div>

        <div data-testid={`${testIdPrefix}-unit-switcher`}>
          <span className="block text-[11px] tracking-wide uppercase text-brand-text-secondary/70 mb-2">
            Unit
          </span>
          <div className="flex rounded-sm border border-brand-border overflow-hidden">
            {UNIT_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setUnit(option.id)}
                className={`px-4 py-3 text-[12px] font-medium transition-colors duration-200 ${
                  unit === option.id
                    ? "bg-brand-gold/10 text-brand-gold"
                    : "text-brand-text-secondary hover:text-brand-text"
                }`}
                data-testid={`${testIdPrefix}-unit-${option.id}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-7">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-text-secondary/70">
          <SwatchBook size={13} className="text-brand-gold" />
          Material
        </div>
        <div className="mt-3 space-y-2.5">
          {materials.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setMaterial(item.id)}
              className={`w-full rounded-sm border px-4 py-4 text-left transition-[border-color,color,background-color] duration-200 ${
                material === item.id
                  ? "border-brand-gold bg-brand-gold/5"
                  : "border-brand-border hover:border-brand-text-secondary/35"
              }`}
              data-testid={`${testIdPrefix}-material-${item.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className={`text-[14px] font-medium ${material === item.id ? "text-brand-gold" : "text-brand-text"}`}>
                    {item.label}
                  </p>
                  <p className="mt-1 text-[11px] text-brand-text-secondary/55 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
                <span className="shrink-0 text-[13px] font-medium text-brand-text-secondary tabular-nums">
                  £{item.rate}/m²
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-7 rounded-sm border border-white/[0.05] bg-brand-bg/70 p-5" data-testid={`${testIdPrefix}-summary`}>
        {areaSqm > 0 ? (
          <>
            <div className="flex items-center gap-2 text-[13px] text-brand-text-secondary/70">
              <Ruler size={14} className="text-brand-gold" />
              <span data-testid={`${testIdPrefix}-dimensions-text`}>{dimensionsText}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-[13px] text-brand-text-secondary/60">
              <Maximize2 size={14} className="text-brand-gold" />
              <span data-testid={`${testIdPrefix}-area-value`}>{areaSqm.toFixed(2)} m²</span>
            </div>
            <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-brand-gold">Estimated Price</p>
                <p
                  className="mt-1 font-heading text-4xl text-brand-text tracking-tight"
                  data-testid={`${testIdPrefix}-estimate-value`}
                >
                  £{estimatedPrice.toFixed(2)}
                </p>
              </div>
              <Link
                to={quoteHref}
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 rounded-sm bg-brand-gold px-6 py-3 text-[13px] font-medium tracking-wide text-brand-bg transition-colors duration-300 hover:bg-brand-gold-hover"
                data-testid={`${testIdPrefix}-quote-cta`}
              >
                {ctaLabel} <ArrowRight size={15} />
              </Link>
            </div>
            <p className="mt-3 text-[11px] leading-snug text-brand-text-secondary/40" data-testid={`${testIdPrefix}-note`}>
              Final pricing is confirmed after artwork review. Installation and wall prep are quoted separately when needed.
            </p>
          </>
        ) : (
          <div className="py-4 text-center" data-testid={`${testIdPrefix}-empty-state`}>
            <p className="text-[14px] text-brand-text-secondary/45">
              Enter your wall width and height to generate a live estimate.
            </p>
            <p className="mt-2 text-[12px] text-brand-text-secondary/30">
              We scale every mural to fit your exact space.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
